import { format } from 'date-fns';

const WARSAW_TIMEZONE = 'Europe/Warsaw';

/**
 * Format date in Warsaw timezone with custom format
 * @param {Date|string} utcDate - UTC date from server
 * @param {string} formatString - date-fns format string
 * @returns {string} Formatted date string
 */
export const formatInWarsaw = (utcDate, formatString = 'dd.MM.yyyy HH:mm') => {
  if (!utcDate) return '';
  
  // Use the browser's built-in timezone support
  const date = new Date(utcDate);
  const warsawFormatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: WARSAW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const parts = warsawFormatter.formatToParts(date);
  const partsObj = parts.reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  
  // Convert to the requested format
  if (formatString === 'dd.MM.yyyy HH:mm') {
    return `${partsObj.day}.${partsObj.month}.${partsObj.year} ${partsObj.hour}:${partsObj.minute}`;
  } else if (formatString === 'dd/MM/yyyy HH:mm') {
    return `${partsObj.day}/${partsObj.month}/${partsObj.year} ${partsObj.hour}:${partsObj.minute}`;
  } else if (formatString === 'dd/MM/yyyy') {
    return `${partsObj.day}/${partsObj.month}/${partsObj.year}`;
  } else if (formatString === 'dd.MM.yyyy') {
    return `${partsObj.day}.${partsObj.month}.${partsObj.year}`;
  } else {
    // For custom formats, use basic conversion
    const warsawString = date.toLocaleString('en-CA', {
      timeZone: WARSAW_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Parse and format with date-fns
    const [datePart, timePart] = warsawString.split(', ');
    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');
    
    // Create a temporary date object for formatting (this is local time, but we're just using it for formatting)
    const tempDate = new Date(year, month - 1, day, hour, minute, second);
    return format(tempDate, formatString);
  }
};

/**
 * Check if a date (in UTC) is expired relative to current Warsaw time
 * @param {Date|string} utcDeadline - UTC deadline from server
 * @returns {boolean} True if expired
 */
export const isExpiredInWarsaw = (utcDeadline) => {
  if (!utcDeadline) return false;
  
  const deadline = new Date(utcDeadline);
  const now = new Date();
  
  return deadline < now;
};

/**
 * Get current time in Warsaw timezone
 * @returns {Date} Current date/time in Warsaw timezone (as local Date object)
 */
export const getCurrentWarsawTime = () => {
  const now = new Date();
  // Return the current time - this is already correct as isExpiredInWarsaw works with UTC
  return now;
};